import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ArrowLeft, Download, Save, Trash2, Image, Type, 
  ZoomIn, ZoomOut, Copy, Layers,
  AlignLeft, AlignCenter, AlignRight,
  ChevronUp, ChevronDown, Lock, Unlock,
  Palette, GripVertical, Move
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = '/kardif-logo.png';

const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
const MARGIN = 40;
const CONTENT_WIDTH = PAGE_WIDTH - (MARGIN * 2);

const COLORS = ['#8B7355', '#1A1A1A', '#D4C5A9', '#FFFFFF', '#000000', '#2563EB', '#059669', '#DC2626'];

function ReportEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  
  const [intervention, setIntervention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [zoom, setZoom] = useState(65);
  
  // Drag state pour TOUCH + MOUSE
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragHandle, setDragHandle] = useState(null);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [startEl, setStartEl] = useState({ x: 0, y: 0, w: 0, h: 0 });
  
  const [editingId, setEditingId] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    loadIntervention();
  }, [id]);

  const loadIntervention = async () => {
    try {
      const res = await axios.get(`${API}/interventions/${id}`);
      setIntervention(res.data);
      setElements(createElements(res.data));
    } catch (e) {
      toast.error('Erreur de chargement');
      nav(-1);
    } finally {
      setLoading(false);
    }
  };

  // ===== CRÉATION DES ÉLÉMENTS =====
  const createElements = (data) => {
    const els = [];
    let y = MARGIN;
    let page = 0;
    
    const nextPage = () => { page++; y = MARGIN + 60; };
    const checkSpace = (needed) => { if (y + needed > PAGE_HEIGHT - MARGIN) nextPage(); };

    // PAGE 1 - Logo
    els.push({ id: 'logo-1', type: 'image', x: MARGIN, y: MARGIN, width: 120, height: 60, src: LOGO_URL, page: 0, style: { objectFit: 'contain' }, locked: false });
    
    // Titre
    els.push({ id: 'title', type: 'text', x: MARGIN, y: MARGIN + 70, width: CONTENT_WIDTH, height: 35, content: 'COMPTE-RENDU DE FIN DE MISSION', page: 0, style: { fontSize: 15, fontWeight: 'bold', textAlign: 'center', color: '#FFFFFF', backgroundColor: '#8B7355', padding: 8 }, locked: false });
    
    // N° Dossier
    els.push({ id: 'dossier', type: 'text', x: MARGIN, y: MARGIN + 115, width: CONTENT_WIDTH, height: 25, content: `N° Dossier : ${data.numero_dossier || data.id?.slice(0, 12).toUpperCase()}`, page: 0, style: { fontSize: 11, fontWeight: 'bold', textAlign: 'center', color: '#8B7355', backgroundColor: '#F8F5F0', padding: 5 }, locked: false });
    
    y = MARGIN + 155;
    
    // Informations
    els.push({ id: 'info-header', type: 'text', x: MARGIN, y: y, width: CONTENT_WIDTH, height: 22, content: 'INFORMATIONS INTERVENTION', page: 0, style: { fontSize: 10, fontWeight: 'bold', color: '#FFFFFF', backgroundColor: '#8B7355', padding: 5 }, locked: false });
    y += 25;
    
    els.push({ id: 'info-content', type: 'text', x: MARGIN, y: y, width: CONTENT_WIDTH, height: 85, content: `Donneur d'ordre : ${data.donneur_ordre_nom || 'N/A'}\nAdresse : ${data.adresse_complete || 'N/A'}\nClient : ${data.nom_sinistre || 'N/A'}\nTéléphone : ${data.client_telephone || 'N/A'}\nTechnicien : ${data.technician_name || 'N/A'}`, page: 0, style: { fontSize: 9, color: '#1A1A1A', backgroundColor: '#FFF', padding: 8, border: '1px solid #D4C5A9', lineHeight: 1.5 }, locked: false });
    y += 95;
    
    // Photo façade
    if (data.photo_facade) {
      els.push({ id: 'facade-header', type: 'text', x: MARGIN, y: y, width: CONTENT_WIDTH, height: 22, content: 'PHOTO DE LA FAÇADE', page: 0, style: { fontSize: 10, fontWeight: 'bold', color: '#FFFFFF', backgroundColor: '#8B7355', padding: 5 }, locked: false });
      y += 25;
      els.push({ id: 'facade-img', type: 'image', x: (PAGE_WIDTH - 260) / 2, y: y, width: 260, height: 170, src: data.photo_facade, page: 0, style: { objectFit: 'cover', border: '2px solid #D4C5A9' }, locked: false });
      y += 180;
    }
    
    // Désordres
    const pieces = data.pieces_sinistrees || [];
    const photosDesordres = data.photos_desordres || [];
    
    if (pieces.length > 0 || photosDesordres.length > 0) {
      checkSpace(200);
      els.push({ id: 'desordres-header', type: 'text', x: MARGIN, y: y, width: CONTENT_WIDTH, height: 22, content: 'DÉSORDRES OBSERVÉS', page: page, style: { fontSize: 10, fontWeight: 'bold', color: '#FFFFFF', backgroundColor: '#8B7355', padding: 5 }, locked: false });
      y += 27;
      
      if (pieces.length > 0) {
        let txt = pieces.map(p => `• ${p.piece || 'Pièce'} : ${(p.stigmates || []).join(', ') || 'Aucun'}`).join('\n');
        if (data.constats_arrivee) txt += `\n\nConstats : ${data.constats_arrivee}`;
        els.push({ id: 'desordres-text', type: 'text', x: MARGIN, y: y, width: CONTENT_WIDTH, height: 60, content: txt, page: page, style: { fontSize: 9, color: '#1A1A1A', padding: 8, backgroundColor: '#FAFAFA', border: '1px solid #E5E5E5' }, locked: false });
        y += 65;
      }
      
      photosDesordres.slice(0, 6).forEach((photo, i) => {
        const col = i % 3;
        const row = Math.floor(i / 3);
        if (row > 0 && col === 0) checkSpace(130);
        els.push({ id: `desordre-photo-${i}`, type: 'image', x: MARGIN + col * 175, y: y + row * 125, width: 165, height: 110, src: photo, caption: `Photo ${i + 1}`, page: page, style: { objectFit: 'cover', border: '1px solid #D4C5A9' }, locked: false });
      });
      const rows = Math.ceil(Math.min(photosDesordres.length, 6) / 3);
      y += rows * 125 + 15;
    }
    
    // Mesures d'humidité
    const mesuresHum = (data.mesures_humidite || []).filter(m => m.zone || m.valeur);
    if (mesuresHum.length > 0) {
      nextPage();
      els.push({ id: 'logo-hum', type: 'image', x: MARGIN, y: MARGIN, width: 100, height: 50, src: LOGO_URL, page: page, style: { objectFit: 'contain' }, locked: true });
      els.push({ id: 'hum-header', type: 'text', x: MARGIN, y: y, width: CONTENT_WIDTH, height: 25, content: "MESURES D'HUMIDITÉ", page: page, style: { fontSize: 11, fontWeight: 'bold', textAlign: 'center', color: '#FFFFFF', backgroundColor: '#8B7355', padding: 6 }, locked: false });
      y += 30;
      
      let tableText = 'Zone | Support | Valeur | État\n─────────────────────────\n';
      mesuresHum.forEach(m => {
        const val = m.valeur || 'N/A';
        let etat = '-';
        try { const v = parseFloat(String(val).replace('%', '')); etat = v < 30 ? 'Normal' : v < 60 ? 'Élevé' : 'Critique'; } catch {}
        tableText += `${m.zone || 'N/A'} | ${m.support || 'N/A'} | ${val} | ${etat}\n`;
      });
      els.push({ id: 'hum-table', type: 'text', x: MARGIN, y: y, width: CONTENT_WIDTH, height: 30 + mesuresHum.length * 16, content: tableText, page: page, style: { fontSize: 8, color: '#1A1A1A', backgroundColor: '#F8F5F0', padding: 8, fontFamily: 'monospace', border: '1px solid #D4C5A9' }, locked: false });
      y += 35 + mesuresHum.length * 16;
      
      const humPhotos = mesuresHum.flatMap(m => m.photos || []);
      humPhotos.slice(0, 4).forEach((photo, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        els.push({ id: `hum-photo-${i}`, type: 'image', x: MARGIN + col * 190 + 60, y: y + row * 135, width: 170, height: 120, src: photo, caption: `Mesure ${i + 1}`, page: page, style: { objectFit: 'cover', border: '1px solid #8B7355' }, locked: false });
      });
      if (humPhotos.length > 0) y += Math.ceil(Math.min(humPhotos.length, 4) / 2) * 135 + 15;
    }
    
    // Mesures thermiques
    const mesuresTherm = (data.mesures_thermiques || []).filter(m => m.zone || m.observation);
    if (mesuresTherm.length > 0) {
      checkSpace(150);
      els.push({ id: 'therm-header', type: 'text', x: MARGIN, y: y, width: CONTENT_WIDTH, height: 25, content: "MESURES THERMIQUES", page: page, style: { fontSize: 11, fontWeight: 'bold', textAlign: 'center', color: '#FFFFFF', backgroundColor: '#DC2626', padding: 6 }, locked: false });
      y += 30;
      mesuresTherm.forEach((m, i) => {
        els.push({ id: `therm-item-${i}`, type: 'text', x: MARGIN, y: y, width: CONTENT_WIDTH, height: 35, content: `Zone : ${m.zone || 'N/A'}\nObservation : ${m.observation || 'N/A'}`, page: page, style: { fontSize: 9, color: '#1A1A1A', backgroundColor: '#FEF2F2', padding: 6, border: '1px solid #FCA5A5' }, locked: false });
        y += 40;
      });
      const thermPhotos = mesuresTherm.flatMap(m => m.photos || []);
      thermPhotos.slice(0, 4).forEach((photo, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        els.push({ id: `therm-photo-${i}`, type: 'image', x: MARGIN + col * 190 + 60, y: y + row * 135, width: 170, height: 120, src: photo, caption: `Thermique ${i + 1}`, page: page, style: { objectFit: 'cover', border: '2px solid #DC2626' }, locked: false });
      });
      if (thermPhotos.length > 0) y += Math.ceil(Math.min(thermPhotos.length, 4) / 2) * 135 + 15;
    }
    
    // Procédés
    const procedes = (data.procedes || []).filter(p => p.type);
    if (procedes.length > 0) {
      nextPage();
      els.push({ id: 'logo-proc', type: 'image', x: MARGIN, y: MARGIN, width: 100, height: 50, src: LOGO_URL, page: page, style: { objectFit: 'contain' }, locked: true });
      els.push({ id: 'proc-header', type: 'text', x: MARGIN, y: y, width: CONTENT_WIDTH, height: 28, content: 'PROCÉDÉS EFFECTUÉS', page: page, style: { fontSize: 12, fontWeight: 'bold', textAlign: 'center', color: '#FFFFFF', backgroundColor: '#8B7355', padding: 7 }, locked: false });
      y += 35;
      
      procedes.forEach((proc, pi) => {
        checkSpace(200);
        els.push({ id: `proc-title-${pi}`, type: 'text', x: MARGIN, y: y, width: CONTENT_WIDTH, height: 24, content: proc.type, page: page, style: { fontSize: 10, fontWeight: 'bold', color: '#8B7355', backgroundColor: '#F8F5F0', padding: 6, border: '1px solid #D4C5A9' }, locked: false });
        y += 28;
        if (proc.description) {
          els.push({ id: `proc-desc-${pi}`, type: 'text', x: MARGIN, y: y, width: CONTENT_WIDTH, height: 45, content: proc.description, page: page, style: { fontSize: 9, color: '#1A1A1A', padding: 8, lineHeight: 1.4 }, locked: false });
          y += 50;
        }
        if (proc.resultat) {
          els.push({ id: `proc-result-${pi}`, type: 'text', x: MARGIN, y: y, width: CONTENT_WIDTH, height: 22, content: `Résultat : ${proc.resultat}`, page: page, style: { fontSize: 9, fontWeight: 'bold', color: '#059669', padding: 5 }, locked: false });
          y += 27;
        }
        const procPhotos = proc.photos || [];
        procPhotos.slice(0, 4).forEach((photo, i) => {
          const col = i % 2;
          const row = Math.floor(i / 2);
          if (row > 0 && col === 0) checkSpace(145);
          els.push({ id: `proc-${pi}-photo-${i}`, type: 'image', x: MARGIN + col * 190 + 60, y: y + row * 140, width: 175, height: 125, src: photo, caption: `${proc.type} - Photo ${i + 1}`, page: page, style: { objectFit: 'cover', border: '2px solid #8B7355' }, locked: false });
        });
        if (procPhotos.length > 0) y += Math.ceil(Math.min(procPhotos.length, 4) / 2) * 140 + 20;
        y += 15;
      });
    }
    
    // Conclusion
    nextPage();
    els.push({ id: 'logo-concl', type: 'image', x: MARGIN, y: MARGIN, width: 100, height: 50, src: LOGO_URL, page: page, style: { objectFit: 'contain' }, locked: true });
    els.push({ id: 'concl-header', type: 'text', x: MARGIN, y: y, width: CONTENT_WIDTH, height: 22, content: 'CONCLUSION', page: page, style: { fontSize: 10, fontWeight: 'bold', color: '#FFFFFF', backgroundColor: '#8B7355', padding: 5 }, locked: false });
    y += 27;
    els.push({ id: 'concl-text', type: 'text', x: MARGIN, y: y, width: CONTENT_WIDTH, height: 120, content: `Synthèse : ${data.resume_constats || 'N/A'}\n\nOrigine probable : ${data.origine_probable || 'N/A'}\n\nPréconisations : ${data.preconisations || 'N/A'}${data.points_urgents ? `\n\n⚠️ Points urgents : ${data.points_urgents}` : ''}`, page: page, style: { fontSize: 9, color: '#1A1A1A', padding: 10, lineHeight: 1.5, border: '1px solid #D4C5A9', backgroundColor: '#FAFAFA' }, locked: false });
    y += 135;
    
    // Validation
    els.push({ id: 'valid-header', type: 'text', x: MARGIN, y: y, width: CONTENT_WIDTH, height: 22, content: 'VALIDATION', page: page, style: { fontSize: 10, fontWeight: 'bold', color: '#FFFFFF', backgroundColor: '#8B7355', padding: 5 }, locked: false });
    y += 27;
    
    if (data.satisfaction_note != null) {
      const color = data.satisfaction_note >= 7 ? '#059669' : data.satisfaction_note >= 5 ? '#F59E0B' : '#DC2626';
      els.push({ id: 'satisfaction', type: 'text', x: MARGIN, y: y, width: CONTENT_WIDTH, height: 30, content: `Satisfaction client : ${data.satisfaction_note}/10`, page: page, style: { fontSize: 12, fontWeight: 'bold', color: color, backgroundColor: '#F8F5F0', padding: 8, textAlign: 'center' }, locked: false });
      y += 40;
    }
    
    if (data.client_signature) {
      els.push({ id: 'sig-label', type: 'text', x: MARGIN, y: y, width: 150, height: 18, content: 'Signature du client :', page: page, style: { fontSize: 9, fontWeight: 'bold', color: '#8B7355' }, locked: false });
      y += 22;
      els.push({ id: 'sig-img', type: 'image', x: MARGIN, y: y, width: 150, height: 80, src: data.client_signature, page: page, style: { objectFit: 'contain', border: '1px solid #D4C5A9', backgroundColor: '#FFF' }, locked: false });
    }
    
    return els;
  };

  // ===== GESTION TOUCH + MOUSE =====
  
  const getEventPos = (e) => {
    if (e.touches && e.touches.length > 0) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    return { x: e.clientX, y: e.clientY };
  };

  const handleStart = (e, elId, handle = null) => {
    e.preventDefault();
    e.stopPropagation();
    
    const el = elements.find(x => x.id === elId);
    if (!el || el.locked) return;
    
    setSelectedId(elId);
    
    const pos = getEventPos(e);
    setStartPos(pos);
    setStartEl({ x: el.x, y: el.y, w: el.width, h: el.height });
    
    if (handle) {
      setIsResizing(true);
      setDragHandle(handle);
    } else {
      setIsDragging(true);
    }
  };

  const handleMove = (e) => {
    if (!isDragging && !isResizing) return;
    if (!selectedId) return;
    
    e.preventDefault();
    
    const pos = getEventPos(e);
    const scale = zoom / 100;
    const dx = (pos.x - startPos.x) / scale;
    const dy = (pos.y - startPos.y) / scale;
    
    setElements(prev => prev.map(el => {
      if (el.id !== selectedId) return el;
      
      if (isDragging) {
        return {
          ...el,
          x: Math.max(0, Math.round(startEl.x + dx)),
          y: Math.max(0, Math.round(startEl.y + dy)),
        };
      }
      
      if (isResizing && dragHandle) {
        let newX = startEl.x, newY = startEl.y, newW = startEl.w, newH = startEl.h;
        const h = dragHandle;
        
        if (h.includes('e')) newW = Math.max(40, Math.round(startEl.w + dx));
        if (h.includes('w')) { newW = Math.max(40, Math.round(startEl.w - dx)); newX = Math.round(startEl.x + dx); }
        if (h.includes('s')) newH = Math.max(25, Math.round(startEl.h + dy));
        if (h.includes('n')) { newH = Math.max(25, Math.round(startEl.h - dy)); newY = Math.round(startEl.y + dy); }
        
        return { ...el, x: Math.max(0, newX), y: Math.max(0, newY), width: newW, height: newH };
      }
      
      return el;
    }));
  };

  const handleEnd = () => {
    setIsDragging(false);
    setIsResizing(false);
    setDragHandle(null);
  };

  // Listeners globaux pour mouse/touch
  useEffect(() => {
    if (isDragging || isResizing) {
      const onMove = (e) => handleMove(e);
      const onEnd = () => handleEnd();
      
      // Mouse
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onEnd);
      // Touch
      window.addEventListener('touchmove', onMove, { passive: false });
      window.addEventListener('touchend', onEnd);
      window.addEventListener('touchcancel', onEnd);
      
      return () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onEnd);
        window.removeEventListener('touchmove', onMove);
        window.removeEventListener('touchend', onEnd);
        window.removeEventListener('touchcancel', onEnd);
      };
    }
  }, [isDragging, isResizing, selectedId, startPos, startEl, dragHandle, zoom]);

  // ===== ACTIONS =====
  
  const addText = () => {
    const maxPage = Math.max(...elements.map(e => e.page || 0), 0);
    const newEl = { id: `text-${Date.now()}`, type: 'text', x: 100, y: 200, width: 200, height: 40, content: 'Nouveau texte', page: maxPage, style: { fontSize: 10, color: '#1A1A1A', padding: 8 }, locked: false };
    setElements(prev => [...prev, newEl]);
    setSelectedId(newEl.id);
  };
  
  const addImage = (src) => {
    const maxPage = Math.max(...elements.map(e => e.page || 0), 0);
    const newEl = { id: `img-${Date.now()}`, type: 'image', x: 100, y: 200, width: 180, height: 130, src: src, page: maxPage, style: { objectFit: 'cover' }, locked: false };
    setElements(prev => [...prev, newEl]);
    setSelectedId(newEl.id);
  };
  
  const duplicate = () => {
    if (!selectedId) return;
    const el = elements.find(e => e.id === selectedId);
    if (!el) return;
    const newEl = { ...el, id: `${el.type}-${Date.now()}`, x: el.x + 15, y: el.y + 15 };
    setElements(prev => [...prev, newEl]);
    setSelectedId(newEl.id);
  };
  
  const remove = () => {
    if (!selectedId) return;
    setElements(prev => prev.filter(e => e.id !== selectedId));
    setSelectedId(null);
  };
  
  const moveLayer = (dir) => {
    if (!selectedId) return;
    setElements(prev => {
      const idx = prev.findIndex(e => e.id === selectedId);
      if (idx === -1) return prev;
      const arr = [...prev];
      if (dir === 'up' && idx < arr.length - 1) [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
      if (dir === 'down' && idx > 0) [arr[idx], arr[idx - 1]] = [arr[idx - 1], arr[idx]];
      return arr;
    });
  };
  
  const toggleLock = () => {
    if (!selectedId) return;
    setElements(prev => prev.map(e => e.id === selectedId ? { ...e, locked: !e.locked } : e));
  };
  
  const updateStyle = (key, val) => {
    if (!selectedId) return;
    setElements(prev => prev.map(e => e.id === selectedId ? { ...e, style: { ...e.style, [key]: val } } : e));
  };

  // ===== SAVE & EXPORT =====
  
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/interventions/${id}`, { report_layout: { elements } });
      toast.success('Sauvegardé !');
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };
  
  const exportPDF = async () => {
    setGenerating(true);
    try {
      await save();
      const res = await axios.post(`${API}/interventions/${id}/generate-report?format=pdf`, {
        editor_settings: { primary_color: '#8B7355', secondary_color: '#D4C5A9' }
      }, { responseType: 'blob' });
      
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `KARDIF_CR_${intervention?.numero_dossier || id.slice(0, 8)}.pdf`;
      a.click();
      toast.success('PDF téléchargé !');
    } catch { toast.error('Erreur PDF'); }
    finally { setGenerating(false); }
  };

  // ===== RENDER =====
  
  const selectedEl = elements.find(e => e.id === selectedId);
  const totalPages = Math.max(...elements.map(e => e.page || 0), 0) + 1;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#8B7355] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col touch-none">
      {/* Toolbar */}
      <header className="bg-gray-800 border-b border-gray-700 px-2 py-2 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => nav(-1)} className="text-gray-300 px-2">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          
          <button onClick={addText} className="p-2 rounded bg-gray-700 text-gray-300 active:bg-gray-600" title="Texte">
            <Type className="h-5 w-5" />
          </button>
          <label className="p-2 rounded bg-gray-700 text-gray-300 active:bg-gray-600 cursor-pointer" title="Image">
            <Image className="h-5 w-5" />
            <input type="file" accept="image/*" className="hidden" onChange={(e) => {
              const f = e.target.files[0];
              if (f) { const r = new FileReader(); r.onload = (ev) => addImage(ev.target.result); r.readAsDataURL(f); }
            }} />
          </label>
          
          {selectedId && (
            <>
              <div className="h-6 w-px bg-gray-600" />
              <button onClick={duplicate} className="p-2 rounded text-gray-400 active:text-white"><Copy className="h-5 w-5" /></button>
              <button onClick={remove} className="p-2 rounded text-red-400 active:text-red-300"><Trash2 className="h-5 w-5" /></button>
              <button onClick={() => moveLayer('up')} className="p-2 rounded text-gray-400 active:text-white"><ChevronUp className="h-5 w-5" /></button>
              <button onClick={() => moveLayer('down')} className="p-2 rounded text-gray-400 active:text-white"><ChevronDown className="h-5 w-5" /></button>
              <button onClick={toggleLock} className="p-2 rounded text-gray-400 active:text-white">
                {selectedEl?.locked ? <Lock className="h-5 w-5" /> : <Unlock className="h-5 w-5" />}
              </button>
            </>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-gray-700 rounded px-2 py-1">
            <button onClick={() => setZoom(z => Math.max(40, z - 10))} className="text-gray-400 active:text-white p-1"><ZoomOut className="h-4 w-4" /></button>
            <span className="text-white text-sm w-10 text-center">{zoom}%</span>
            <button onClick={() => setZoom(z => Math.min(120, z + 10))} className="text-gray-400 active:text-white p-1"><ZoomIn className="h-4 w-4" /></button>
          </div>
          
          <Button onClick={save} disabled={saving} variant="outline" size="sm" className="border-gray-600 text-gray-300 px-3">
            <Save className="h-4 w-4 mr-1" />{saving ? '...' : 'Sauver'}
          </Button>
          
          <Button onClick={exportPDF} disabled={generating} className="bg-[#8B7355] active:bg-[#7A6548] px-3">
            <Download className="h-4 w-4 mr-1" />{generating ? '...' : 'PDF'}
          </Button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Panneau gauche - Liste des éléments */}
        <aside className="w-48 bg-gray-800 border-r border-gray-700 flex flex-col overflow-hidden">
          <div className="p-2 border-b border-gray-700">
            <h3 className="text-white text-xs flex items-center gap-1">
              <Layers className="h-3 w-3 text-[#8B7355]" />Éléments ({elements.length})
            </h3>
          </div>
          
          <div className="flex-1 overflow-y-auto p-1 space-y-0.5">
            {elements.map(el => (
              <div
                key={el.id}
                onClick={() => setSelectedId(el.id)}
                className={`p-1.5 rounded cursor-pointer flex items-center gap-1 text-xs ${
                  selectedId === el.id ? 'bg-[#8B7355] text-white' : 'text-gray-400 active:bg-gray-700'
                }`}
              >
                <GripVertical className="h-3 w-3 opacity-50" />
                {el.type === 'text' ? <Type className="h-3 w-3" /> : <Image className="h-3 w-3" />}
                <span className="truncate flex-1">{el.type === 'text' ? el.content?.slice(0, 12) : el.caption || 'Image'}</span>
                <span className="text-[9px] opacity-50">P{(el.page || 0) + 1}</span>
              </div>
            ))}
          </div>
          
          {/* Photos disponibles */}
          <div className="border-t border-gray-700 p-2">
            <h4 className="text-gray-500 text-xs mb-1">Photos</h4>
            <div className="grid grid-cols-3 gap-1 max-h-20 overflow-y-auto">
              {[intervention?.photo_facade, ...(intervention?.photos_desordres || []), ...(intervention?.procedes?.flatMap(p => p.photos || []) || [])].filter(Boolean).slice(0, 9).map((p, i) => (
                <img key={i} src={p} className="w-full aspect-square object-cover rounded cursor-pointer active:ring-2 active:ring-[#8B7355]" onClick={() => addImage(p)} />
              ))}
            </div>
          </div>
        </aside>

        {/* Canvas - TOUCH ENABLED */}
        <main className="flex-1 overflow-auto bg-gray-600 p-4" ref={canvasRef}>
          <div className="flex flex-col items-center gap-6">
            {Array.from({ length: totalPages }).map((_, pageIdx) => (
              <div key={pageIdx}>
                <div className="text-gray-300 text-xs mb-1 text-center font-medium">Page {pageIdx + 1} / {totalPages}</div>
                <div
                  className="relative bg-white shadow-xl"
                  style={{ width: PAGE_WIDTH * (zoom / 100), height: PAGE_HEIGHT * (zoom / 100) }}
                  onClick={() => setSelectedId(null)}
                  onTouchStart={() => {}}
                >
                  {/* Éléments */}
                  {elements.filter(el => (el.page || 0) === pageIdx).map(el => (
                    <div
                      key={el.id}
                      className={`absolute ${el.locked ? 'opacity-70' : ''} ${selectedId === el.id ? 'ring-4 ring-blue-500' : ''}`}
                      style={{
                        left: el.x * (zoom / 100),
                        top: el.y * (zoom / 100),
                        width: el.width * (zoom / 100),
                        height: el.height * (zoom / 100),
                        zIndex: selectedId === el.id ? 100 : 1,
                        touchAction: 'none',
                      }}
                      onMouseDown={(e) => handleStart(e, el.id)}
                      onTouchStart={(e) => handleStart(e, el.id)}
                      onDoubleClick={() => el.type === 'text' && !el.locked && setEditingId(el.id)}
                    >
                      {el.type === 'text' ? (
                        editingId === el.id ? (
                          <textarea
                            autoFocus
                            value={el.content}
                            onChange={(e) => setElements(prev => prev.map(x => x.id === el.id ? { ...x, content: e.target.value } : x))}
                            onBlur={() => setEditingId(null)}
                            className="w-full h-full resize-none outline-none border-2 border-blue-500 p-1 text-xs"
                            style={{
                              fontSize: (el.style.fontSize || 10) * (zoom / 100),
                              fontWeight: el.style.fontWeight,
                              color: el.style.color,
                              backgroundColor: el.style.backgroundColor || '#fff',
                              textAlign: el.style.textAlign,
                            }}
                          />
                        ) : (
                          <div className="w-full h-full overflow-hidden whitespace-pre-wrap select-none" style={{
                            fontSize: (el.style.fontSize || 10) * (zoom / 100),
                            fontWeight: el.style.fontWeight,
                            color: el.style.color,
                            backgroundColor: el.style.backgroundColor,
                            textAlign: el.style.textAlign,
                            padding: (el.style.padding || 0) * (zoom / 100),
                            border: el.style.border,
                            lineHeight: el.style.lineHeight || 1.3,
                          }}>
                            {el.content}
                          </div>
                        )
                      ) : (
                        <div className="w-full h-full relative select-none">
                          <img src={el.src} alt="" className="w-full h-full pointer-events-none" draggable={false} style={{
                            objectFit: el.style.objectFit || 'cover',
                            border: el.style.border,
                          }} />
                          {el.caption && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-center" style={{ fontSize: 7 * (zoom / 100), padding: 2 * (zoom / 100) }}>
                              {el.caption}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Poignées de redimensionnement - PLUS GROSSES pour tactile */}
                      {selectedId === el.id && !el.locked && (
                        <>
                          {['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'].map(h => (
                            <div
                              key={h}
                              className="absolute bg-blue-500 border-2 border-white rounded-full z-50 shadow-lg"
                              style={{
                                width: 20,
                                height: 20,
                                cursor: h === 'n' || h === 's' ? 'ns-resize' : h === 'e' || h === 'w' ? 'ew-resize' : h === 'nw' || h === 'se' ? 'nwse-resize' : 'nesw-resize',
                                top: h.includes('n') ? -10 : h.includes('s') ? 'auto' : '50%',
                                bottom: h.includes('s') ? -10 : 'auto',
                                left: h.includes('w') ? -10 : h.includes('e') ? 'auto' : '50%',
                                right: h.includes('e') ? -10 : 'auto',
                                transform: (h === 'n' || h === 's') ? 'translateX(-50%)' : (h === 'e' || h === 'w') ? 'translateY(-50%)' : 'none',
                                touchAction: 'none',
                              }}
                              onMouseDown={(e) => handleStart(e, el.id, h)}
                              onTouchStart={(e) => handleStart(e, el.id, h)}
                            />
                          ))}
                          
                          {/* Indicateur de déplacement au centre */}
                          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-500 rounded-full p-2 shadow-lg z-50 pointer-events-none">
                            <Move className="h-5 w-5 text-white" />
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </main>

        {/* Panneau droit - Propriétés */}
        <aside className="w-52 bg-gray-800 border-l border-gray-700 overflow-y-auto">
          <div className="p-2 border-b border-gray-700">
            <h3 className="text-white text-xs flex items-center gap-1">
              <Palette className="h-3 w-3 text-[#8B7355]" />Propriétés
            </h3>
          </div>
          
          {selectedEl ? (
            <div className="p-2 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-gray-500 text-[10px]">X</span>
                  <Input type="number" value={Math.round(selectedEl.x)} onChange={(e) => setElements(p => p.map(el => el.id === selectedId ? { ...el, x: +e.target.value } : el))} className="bg-gray-700 border-gray-600 text-white h-8 text-sm" />
                </div>
                <div>
                  <span className="text-gray-500 text-[10px]">Y</span>
                  <Input type="number" value={Math.round(selectedEl.y)} onChange={(e) => setElements(p => p.map(el => el.id === selectedId ? { ...el, y: +e.target.value } : el))} className="bg-gray-700 border-gray-600 text-white h-8 text-sm" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-gray-500 text-[10px]">Largeur</span>
                  <Input type="number" value={Math.round(selectedEl.width)} onChange={(e) => setElements(p => p.map(el => el.id === selectedId ? { ...el, width: +e.target.value } : el))} className="bg-gray-700 border-gray-600 text-white h-8 text-sm" />
                </div>
                <div>
                  <span className="text-gray-500 text-[10px]">Hauteur</span>
                  <Input type="number" value={Math.round(selectedEl.height)} onChange={(e) => setElements(p => p.map(el => el.id === selectedId ? { ...el, height: +e.target.value } : el))} className="bg-gray-700 border-gray-600 text-white h-8 text-sm" />
                </div>
              </div>
              
              <div>
                <span className="text-gray-500 text-[10px]">Page</span>
                <Input type="number" value={(selectedEl.page || 0) + 1} min={1} max={totalPages} onChange={(e) => setElements(p => p.map(el => el.id === selectedId ? { ...el, page: Math.max(0, +e.target.value - 1) } : el))} className="bg-gray-700 border-gray-600 text-white h-8 text-sm" />
              </div>
              
              {selectedEl.type === 'text' && (
                <>
                  <div>
                    <span className="text-gray-500 text-[10px]">Taille</span>
                    <select value={selectedEl.style.fontSize || 10} onChange={(e) => updateStyle('fontSize', +e.target.value)} className="w-full bg-gray-700 border border-gray-600 text-white rounded px-2 py-1.5 text-sm">
                      {[8, 9, 10, 11, 12, 14, 16, 18, 20, 24].map(s => <option key={s} value={s}>{s}px</option>)}
                    </select>
                  </div>
                  
                  <div>
                    <span className="text-gray-500 text-[10px]">Alignement</span>
                    <div className="flex gap-1 mt-1">
                      {['left', 'center', 'right'].map(a => (
                        <button key={a} onClick={() => updateStyle('textAlign', a)} className={`flex-1 p-2 rounded ${selectedEl.style.textAlign === a ? 'bg-[#8B7355] text-white' : 'bg-gray-700 text-gray-400'}`}>
                          {a === 'left' ? <AlignLeft className="h-4 w-4 mx-auto" /> : a === 'center' ? <AlignCenter className="h-4 w-4 mx-auto" /> : <AlignRight className="h-4 w-4 mx-auto" />}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <span className="text-gray-500 text-[10px]">Couleur</span>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {COLORS.map(c => (
                        <button key={c} onClick={() => updateStyle('color', c)} className={`w-6 h-6 rounded ${selectedEl.style.color === c ? 'ring-2 ring-white' : ''}`} style={{ backgroundColor: c, border: c === '#FFFFFF' ? '1px solid #888' : 'none' }} />
                      ))}
                    </div>
                  </div>
                </>
              )}
              
              {selectedEl.type === 'image' && (
                <div>
                  <span className="text-gray-500 text-[10px]">Ajustement</span>
                  <select value={selectedEl.style.objectFit || 'cover'} onChange={(e) => updateStyle('objectFit', e.target.value)} className="w-full bg-gray-700 border border-gray-600 text-white rounded px-2 py-1.5 text-sm">
                    <option value="cover">Remplir</option>
                    <option value="contain">Contenir</option>
                    <option value="fill">Étirer</option>
                  </select>
                </div>
              )}
              
              <div className="pt-2 border-t border-gray-700 text-center">
                <p className="text-gray-400 text-xs flex items-center justify-center gap-1">
                  <Move className="h-3 w-3" /> Touchez et glissez
                </p>
              </div>
            </div>
          ) : (
            <div className="p-3 text-gray-500 text-sm text-center">
              Touchez un élément pour le modifier
            </div>
          )}
        </aside>
      </div>
      
      <footer className="bg-gray-800 border-t border-gray-700 px-3 py-1.5 text-xs text-gray-400 text-center">
        {elements.length} éléments | {totalPages} pages {selectedEl && `| ${selectedEl.type} (${Math.round(selectedEl.width)}×${Math.round(selectedEl.height)})`}
      </footer>
    </div>
  );
}

export default ReportEditor;
